import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { CheckboxInput, Typography } from "@/components/atoms";
import { useGetVehicleFormOptionsQuery } from "@/redux/apis/vehicle/vehicleApi";
import { FilterDrawer } from "../common/FilterDrawer";
import { FilterPopover } from "../common/FilterPopover";
import { NO_VEHICLE_TYPE_FILTER } from "../types";
import { useVehicleFilters } from "../useVehicleFilters";

const NO_VEHICLE_TYPE_LABEL = "No Vehicle Type";

const schema = z.object({
  vehicleType: z.array(z.string()),
});

type FormData = z.infer<typeof schema>;

/**
 * Selectable options: ACTIVE types only when the payload carries status
 * (API ≥1.1.2) — inactive types can't receive bookings and the tables hide
 * them, so filtering by one would only return "-" rows. When a custom type
 * shares its name with another visible option (e.g. a same-named global),
 * it gets a "(Custom)" suffix so the two entries are distinguishable.
 */
const useVehicleTypeOptions = () => {
  const { isFetching, data: options } = useGetVehicleFormOptionsQuery();

  const types = useMemo(() => {
    if (!options) return [];
    const hasStatus = options.vehicleTypes.every((type) => typeof type.isActive === "boolean");
    const visible = hasStatus ? options.vehicleTypes.filter((type) => type.isActive) : options.vehicleTypes;

    const nameCounts: Record<string, number> = {};
    visible.forEach((type) => {
      nameCounts[type.name] = (nameCounts[type.name] ?? 0) + 1;
    });

    return visible.map((type) => ({
      name: nameCounts[type.name] > 1 && type.isDefault === false ? `${type.name} (Custom)` : type.name,
      value: type.uuid,
    }));
  }, [options]);

  return { isFetching, types };
};

const useVehicleTypeDisplayValue = (selected: (string | null)[] | null | undefined) => {
  const { data: options } = useGetVehicleFormOptionsQuery();

  return useMemo(() => {
    if (!selected) return undefined;
    const names = selected
      .filter(Boolean)
      .map((uuid) =>
        uuid === NO_VEHICLE_TYPE_FILTER ? NO_VEHICLE_TYPE_LABEL : options?.vehicleTypes.find((t) => t.uuid === uuid)?.name
      )
      .filter(Boolean) as string[];
    return names.length > 0 ? names.join(", ") : undefined;
  }, [selected, options]);
};

export const VehicleTypeFilter = () => {
  const {
    filters: { vehicleType },
    setFilter,
  } = useVehicleFilters();

  const value = useVehicleTypeDisplayValue(vehicleType);

  const onClose = () => setFilter("vehicleType", undefined);

  return (
    <FilterPopover name="vehicleType" value={value} label="Vehicle Type" onClose={onClose}>
      <VehicleTypeFilterForm />
    </FilterPopover>
  );
};

interface VehicleTypeFilterDrawerProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const VehicleTypeFilterDrawer = ({ open, onOpenChange }: VehicleTypeFilterDrawerProps) => {
  const {
    filters: { vehicleType },
    setFilter,
  } = useVehicleFilters();

  const value = useVehicleTypeDisplayValue(vehicleType);

  const onClose = () => setFilter("vehicleType", undefined);

  return (
    <FilterDrawer open={open} onOpenChange={onOpenChange} value={value} label="Vehicle Type" onClose={onClose}>
      <VehicleTypeFilterForm />
    </FilterDrawer>
  );
};

const VehicleTypeFilterForm = () => {
  const { setFilter, filters } = useVehicleFilters();
  const { types } = useVehicleTypeOptions();

  const { control, setValue } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { vehicleType: (filters.vehicleType as string[]) ?? [] },
  });

  const handleCheckAll = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { checked } = e.target;

      if (checked) {
        setFilter(
          "vehicleType",
          types.map((type) => type.value)
        );
        setValue(
          "vehicleType",
          types.map((type) => type.value)
        );
      } else {
        setFilter("vehicleType", null);
        setValue("vehicleType", []);
      }
    },
    [types, setFilter, setValue]
  );

  const isAllSelected = useMemo(() => {
    if (!filters.vehicleType || types.length === 0) return false;
    return types.every((type) => filters.vehicleType?.includes(type.value));
  }, [types, filters.vehicleType]);

  return (
    <div className="contents">
      <Typography className="mb-4">Vehicle Type</Typography>
      <CheckboxInput label="All Vehicle Types" onChange={handleCheckAll} variant="check" checked={isAllSelected} />
      <div className="mt-4 grid gap-4 ">
        <Controller
          control={control}
          name="vehicleType"
          render={({ field }) => (
            <>
              {/* Pinned: vehicles with no ACTIVE type attached (the "-"/warning rows). Mutually exclusive with type selections. */}
              <CheckboxInput
                label={NO_VEHICLE_TYPE_LABEL}
                value={NO_VEHICLE_TYPE_FILTER}
                onChange={(e) => {
                  const next = e.target.checked ? [NO_VEHICLE_TYPE_FILTER] : [];
                  field.onChange(next);
                  setFilter("vehicleType", next.length > 0 ? next : null);
                }}
                variant="check"
                checked={field.value.includes(NO_VEHICLE_TYPE_FILTER)}
              />
              {types.map((type) => (
                <CheckboxInput
                  key={type.value}
                  label={type.name}
                  value={type.value}
                  onChange={(e) => {
                    if (e.target.checked) {
                      const next = [...field.value.filter((i) => i !== NO_VEHICLE_TYPE_FILTER), e.target.value];
                      field.onChange(next);
                      setFilter("vehicleType", next.length > 0 ? next : null);
                    } else {
                      const next = field.value.filter((i) => i !== e.target.value);
                      field.onChange(next);
                      setFilter("vehicleType", next.length > 0 ? next : null);
                    }
                  }}
                  variant="check"
                  checked={field.value.includes(type.value)}
                />
              ))}
            </>
          )}
        />
      </div>
    </div>
  );
};
